import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Typography, Button, Tag, Skeleton, Card } from 'antd';
import { ArrowLeftOutlined, ClockCircleOutlined } from '@ant-design/icons';
import PageHeader from '../Layout/PageHeader';
import Comments from './Comments';
import { tc } from '../../i18n/common';
import { tv } from '../../i18n/videoDetail';

const { Title, Text, Paragraph } = Typography;

const VideoDetail = () => {
    const { slug } = useParams();
    const [item, setItem] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(false);
    const videoRef = useRef(null);
    const [descExpanded, setDescExpanded] = useState(false);
    const sentQuartiles = useRef({ q25: false, q50: false, q75: false });

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const locale = localStorage.getItem('locale') || '';
                const [showRes, relatedRes] = await Promise.all([
                    axios.get(`/v1/videos/${slug}` + (locale ? `?locale=${encodeURIComponent(locale)}` : '')),
                    axios.get(`/v1/videos/${slug}/related` + (locale ? `?locale=${encodeURIComponent(locale)}` : '')).catch(() => ({ data: { data: [] } }))
                ]);
                setItem(showRes.data?.data || null);
                setRelated(relatedRes.data?.data || []);
            } catch (e) {
                console.error('Failed to load video', e);
            } finally {
                setLoading(false);
            }
        };
        if (slug) load();
    }, [slug]);

    useEffect(() => {
        // React to language changes from header select
        const onLocale = () => {
            if (!slug) return;
            const current = localStorage.getItem('locale') || '';
            Promise.all([
                axios.get(`/v1/videos/${slug}` + (current ? `?locale=${encodeURIComponent(current)}` : '')),
                axios.get(`/v1/videos/${slug}/related` + (current ? `?locale=${encodeURIComponent(current)}` : ''))
            ]).then(([showRes, relatedRes]) => {
                setItem(showRes.data?.data || null);
                setRelated(relatedRes.data?.data || []);
                // Force re-render of player with new media URL
                setTimeout(() => {
                    const v = videoRef.current; if (v) { v.pause(); v.load(); }
                }, 0);
            }).catch(() => {});
        };
        window.addEventListener('locale-changed', onLocale);
        return () => window.removeEventListener('locale-changed', onLocale);
    }, [slug]);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const send = (event_type, meta = {}) => {
            if (!item) return;
            axios.post('/v1/analytics', {
                entity_type: 'video',
                entity_id: item.id,
                event_type,
                meta
            }).catch(() => {});
        };
        const onPlay = () => send('play');
        const onPause = () => send('pause');
        const onEnded = () => send('complete');
        const onTime = () => {
            if (!item?.duration || !v?.currentTime) return;
            const pct = (v.currentTime / item.duration) * 100;
            if (!sentQuartiles.current.q25 && pct >= 25) { sentQuartiles.current.q25 = true; send('quartile_25'); }
            if (!sentQuartiles.current.q50 && pct >= 50) { sentQuartiles.current.q50 = true; send('quartile_50'); }
            if (!sentQuartiles.current.q75 && pct >= 75) { sentQuartiles.current.q75 = true; send('quartile_75'); }
        };
        v.addEventListener('play', onPlay);
        v.addEventListener('pause', onPause);
        v.addEventListener('ended', onEnded);
        v.addEventListener('timeupdate', onTime);
        return () => {
            v.removeEventListener('play', onPlay);
            v.removeEventListener('pause', onPause);
            v.removeEventListener('ended', onEnded);
            v.removeEventListener('timeupdate', onTime);
        };
    }, [item]);

    const locale = (typeof window !== 'undefined' && localStorage.getItem('locale')) || 'en';
    const t = (k) => tv(k, locale);

    if (loading) {
        return (
            <div style={{
                backgroundColor: '#fafafa',
                minHeight: '100vh'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderBottom: '1px solid #f0f0f0',
                    padding: '24px 0'
                }}>
                    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
                        <Skeleton active paragraph={{ rows: 2 }} />
                    </div>
                </div>
                <div style={{ padding: '40px 24px' }}>
                    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                        <Skeleton active paragraph={{ rows: 8 }} />
                    </div>
                </div>
            </div>
        );
    }

    if (!item) {
        return (
            <div style={{
                backgroundColor: '#fafafa',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Card style={{ textAlign: 'center', maxWidth: 400 }}>
                    <Title level={3}>Video Not Found</Title>
                    <Text type="secondary">The video you're looking for doesn't exist or has been removed.</Text>
                    <div style={{ marginTop: 16 }}>
                        <Link to="/videos">
                            <Button type="primary" icon={<ArrowLeftOutlined />}>
                                Back to Videos
                            </Button>
                        </Link>
                    </div>
                </Card>
            </div>
        );
    }

    const renderPlayer = () => {
        if (item.source_type === 'youtube' && item.source_url) {
            return (
                <iframe
                    width="100%"
                    height="420"
                    src={item.source_url}
                    title={item.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                />
            );
        }
        if (item.source_type === 'vimeo' && item.source_url) {
            return (
                <iframe
                    src={item.source_url}
                    width="100%"
                    height="420"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={item.title}
                />
            );
        }
        const mediaUrl = item.hls_manifest_url || item.source_url;
        if (mediaUrl) {
            return (
                <video key={mediaUrl} ref={videoRef} controls preload="metadata" playsInline style={{ width: '100%', borderRadius: 8, background: '#000' }}>
                    <source src={mediaUrl} />
                </video>
            );
        }
        return <div>{t('processing')}</div>;
    };

    return (
        <div style={{
            backgroundColor: '#fafafa',
            minHeight: '100vh'
        }}>
            <PageHeader
                backButton={{
                    to: "/videos",
                    text: tc('backToVideos', locale)
                }}
                title={item.title}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {item.category && (
                        <Tag color="blue" style={{ fontSize: 12 }}>
                            {item.category}
                        </Tag>
                    )}
                    {item.duration && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                            {Math.floor(item.duration / 60)}:{String(item.duration % 60).padStart(2,'0')}
                        </Text>
                    )}
                    {item.created_at && (new Date(item.created_at) > new Date(Date.now() - 7*24*60*60*1000)) && (
                        <Tag color="green" style={{ fontSize: 10 }}>New</Tag>
                    )}
                </div>
            </PageHeader>

            {/* Content Section */}
            <div style={{ padding: '40px 24px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                    <div className="video-detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 32 }}>
                        <style>{`@media (min-width: 992px){ .video-detail-grid{ grid-template-columns: minmax(0, 1fr) 380px !important; } }`}</style>

                        {/* Main Content */}
                        <div>
                            {/* Video Player */}
                            <Card style={{
                                marginBottom: 32,
                                padding: 0,
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }} styles={{ body: { padding: 0 } }}>
                                {renderPlayer()}
                            </Card>

                            {item.description && (
                                <Card style={{ marginBottom: 32 }}>
                                    <div>
                                        <Paragraph
                                            style={{
                                                whiteSpace: 'pre-wrap',
                                                marginBottom: 8,
                                                maxHeight: descExpanded ? 'none' : 100,
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {item.description}
                                        </Paragraph>
                                        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setDescExpanded(v => !v)}>
                                            {descExpanded ? t('readLess') : t('readMore')}
                                        </Button>
                                    </div>
                                </Card>
                            )}

                            {/* Comments Section */}
                            <Card style={{
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                border: '1px solid #f0f0f0'
                            }}>
                                <Title level={3} style={{ marginTop: 0, marginBottom: 16 }}>
                                    {tc('questions', locale)}
                                </Title>
                                <Comments videoId={item.id} />
                            </Card>
                        </div>

                        {/* Sidebar */}
                        <div>
                            {related.length > 0 && (
                                <Card style={{
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                    border: '1px solid #f0f0f0'
                                }}>
                                    <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
                                        {t('related')}
                                    </Title>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        {related.map(r => (
                                            <Link key={r.id} to={`/videos/${r.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                                <div style={{
                                                    display: 'flex',
                                                    gap: 12,
                                                    padding: 8,
                                                    borderRadius: 8,
                                                    transition: 'background-color 0.2s ease',
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                >
                                                    <div style={{
                                                        width: 120,
                                                        height: 68,
                                                        flex: '0 0 auto',
                                                        borderRadius: 6,
                                                        overflow: 'hidden',
                                                        background: 'transparent'
                                                    }}>
                                                        <img
                                                            src={r.thumbnail_url || '/images/placeholder/video-placeholder.jpg'}
                                                            alt={r.title}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    </div>
                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                        <div style={{
                                                            fontWeight: 600,
                                                            lineHeight: 1.3,
                                                            fontSize: 14,
                                                            color: '#1a1a1a',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            marginBottom: 4
                                                        }}>
                                                            {r.title}
                                                        </div>
                                                        {r.category && (
                                                            <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                                                                {r.category}
                                                            </Tag>
                                                        )}
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </Card>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoDetail;
